import { useCallback, useEffect, useRef, useState } from "react";
import { useOutletContext } from "react-router-dom";
import { toast } from "sonner";
import { Pencil, Trash2, Upload, Download, FileAudio, CheckCircle2, AlertCircle, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { PageHeader, Empty } from "./ui";
import { useBlobUrl } from "@/components/chat/useBlobUrl";
import { errMsg } from "@/lib/api";
import { fmtSize } from "@/lib/format";
import { VideoLibrary } from "./VideoLibrary";
import { SavedLinks } from "./SavedLinks";
import { LIB_CATS, LIB_LABEL, AUDIO_ACCEPT } from "@/lib/library";

const EMPTY = { title: "", text: "", category: "oracao", active: true, audio_id: "" };
const sel = "h-10 rounded-md border border-input bg-white px-2 text-sm";

const ReplyForm = ({ initial, audios, onSave, onCancel }) => {
  const [f, setF] = useState(initial);
  useEffect(() => { setF(initial); }, [initial]);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  return (
    <form data-testid="reply-form" onSubmit={(e) => { e.preventDefault(); onSave({ ...f, audio_id: f.audio_id || null }); }} className="space-y-2 rounded-lg border border-zap-line bg-white p-4">
      <p className="font-semibold">{initial.id ? "Editar resposta" : "Nova resposta"}</p>
      <div className="grid gap-2 sm:grid-cols-2">
        <Input data-testid="reply-title-input" value={f.title} onChange={set("title")} placeholder="Título (ex.: Oração recebida)" maxLength={60} />
        <select data-testid="reply-category-input" value={f.category} onChange={set("category")} className={sel}>{LIB_CATS.map(([k, l]) => <option key={k} value={k}>{l}</option>)}</select>
      </div>
      <Textarea data-testid="reply-text-input" value={f.text} onChange={set("text")} rows={4} placeholder="{primeiro_nome}, recebemos seu pedido de oração. Deus proverá!" />
      <div className="flex flex-wrap items-center gap-3">
        <select data-testid="reply-audio-input" value={f.audio_id || ""} onChange={set("audio_id")} className={sel}>
          <option value="">Sem áudio oficial</option>{audios.map((a) => <option key={a.id} value={a.id}>{a.title}</option>)}
        </select>
        <label className="flex items-center gap-2 text-sm"><Switch data-testid="reply-active-input" checked={f.active} onCheckedChange={(v) => setF({ ...f, active: v })} /> Ativa</label>
      </div>
      <p className="text-xs text-muted-foreground">Use {"{primeiro_nome}"} para inserir o primeiro nome do participante. O texto sempre pode ser revisado antes do envio.</p>
      <div className="flex gap-2">
        <Button data-testid="reply-save" disabled={f.title.trim().length < 2 || f.text.trim().length < 2}>Salvar</Button>
        {initial.id && <Button type="button" variant="ghost" onClick={onCancel}>Cancelar</Button>}
      </div>
    </form>
  );
};

const Replies = ({ audios }) => {
  const { api } = useOutletContext();
  const [cat, setCat] = useState("");
  const [q, setQ] = useState("");
  const [items, setItems] = useState(null);
  const [editing, setEditing] = useState(EMPTY);
  const load = useCallback(() => api.get("/admin/quick-replies", { params: { category: cat || undefined, q: q || undefined } }).then((r) => setItems(r.data)).catch(() => setItems([])), [api, cat, q]);
  useEffect(() => { load(); }, [load]);
  const save = async (body) => {
    try {
      editing.id ? await api.put(`/admin/quick-replies/${editing.id}`, body) : await api.post("/admin/quick-replies", body);
      toast.success("Resposta salva."); setEditing({ ...EMPTY }); load();
    } catch (e) { toast.error(errMsg(e)); }
  };
  const toggle = async (r) => {
    try { await api.put(`/admin/quick-replies/${r.id}`, { ...r, active: !r.active }); load(); } catch (e) { toast.error(errMsg(e)); }
  };
  const del = async (id) => {
    try { await api.delete(`/admin/quick-replies/${id}`); load(); toast.success("Resposta excluída."); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <section className="space-y-3">
      <div className="flex flex-wrap gap-2">
        <select data-testid="library-filter-category" value={cat} onChange={(e) => setCat(e.target.value)} className={sel}>
          <option value="">Todas as categorias</option>{LIB_CATS.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
        </select>
        <Input data-testid="library-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Pesquisar" className="h-10 w-56" />
      </div>
      {items === null ? null : items.length === 0 ? <Empty testId="library-empty">Nenhuma resposta nesta categoria.</Empty> : (
        <ul className="divide-y divide-zap-line rounded-lg border border-zap-line bg-white">
          {items.map((r) => (
            <li key={r.id} data-testid={`library-item-${r.id}`} className={`flex items-start gap-3 px-4 py-3 ${r.active ? "" : "opacity-55"}`}>
              <div className="min-w-0 flex-1">
                <p className="font-semibold">{r.title} <span className="ml-1 rounded bg-zap-deep/10 px-1.5 py-0.5 text-[11px] text-zap-deep">{LIB_LABEL[r.category]}</span>{r.audio_id && <span className="ml-1 text-xs text-[#7A5A14]">+ áudio</span>}</p>
                <p className="whitespace-pre-wrap text-sm text-muted-foreground">{r.text}</p>
              </div>
              <Switch data-testid={`library-toggle-${r.id}`} checked={r.active} onCheckedChange={() => toggle(r)} aria-label="Ativar ou desativar" />
              <button data-testid={`library-edit-${r.id}`} onClick={() => setEditing({ ...EMPTY, ...r, audio_id: r.audio_id || "" })} aria-label="Editar" className="rounded p-2 hover:bg-zap-mist"><Pencil className="h-4 w-4" /></button>
              <button data-testid={`library-delete-${r.id}`} onClick={() => del(r.id)} aria-label="Excluir" className="rounded p-2 text-muted-foreground hover:bg-red-50 hover:text-red-700"><Trash2 className="h-4 w-4" /></button>
            </li>
          ))}
        </ul>
      )}
      <ReplyForm initial={editing} audios={audios} onSave={save} onCancel={() => setEditing({ ...EMPTY })} />
    </section>
  );
};

const AudioRow = ({ a, onDelete }) => {
  const { api } = useOutletContext();
  const { url } = useBlobUrl(api, `/admin/files/${a.file.id}`);
  return (
    <li data-testid={`audio-item-${a.id}`} className="flex flex-wrap items-center gap-3 px-4 py-3">
      <div className="min-w-0 flex-1"><p className="font-semibold">{a.title}</p><p className="text-xs text-muted-foreground">{a.category_slugs.map((c) => LIB_LABEL[c]).join(", ") || "Sem categoria"}</p></div>
      {url ? <audio data-testid={`audio-player-${a.id}`} controls preload="metadata" src={url} className="h-10 w-64 max-w-full" /> : <span className="text-xs text-muted-foreground">Carregando áudio…</span>}
      {url && <a href={url} download={a.file.name} aria-label="Baixar" className="rounded p-2 hover:bg-zap-mist"><Download className="h-4 w-4" /></a>}
      <button data-testid={`audio-delete-${a.id}`} onClick={() => window.confirm("Excluir este áudio oficial?") && onDelete(a.id)} aria-label="Excluir" className="rounded p-2 text-muted-foreground hover:bg-red-50 hover:text-red-700"><Trash2 className="h-4 w-4" /></button>
    </li>
  );
};

const Audios = ({ audios, reload }) => {
  const { api } = useOutletContext();
  const ref = useRef(null);
  const [title, setTitle] = useState("");
  const [cats, setCats] = useState([]);
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [status, setStatus] = useState(null);
  const pick = (e) => {
    const f = e.target.files?.[0] || null;
    e.target.value = "";
    setStatus(null);
    if (!f) return;
    if (f.size > 16 * 1024 * 1024) { setFile(null); setStatus({ ok: false, text: `“${f.name}” tem mais de 16 MB. Escolha um arquivo menor.` }); return; }
    setFile(f);
    if (title.trim().length < 2) setTitle(f.name.replace(/\.[^.]+$/, "").slice(0, 80));
  };
  const upload = async (e) => {
    e.preventDefault();
    if (!file) { setStatus({ ok: false, text: "Escolha um arquivo de áudio." }); return; }
    if (title.trim().length < 2) { setStatus({ ok: false, text: "Escreva um título com pelo menos 2 letras." }); return; }
    const fd = new FormData();
    fd.append("title", title); fd.append("categories", cats.join(",")); fd.append("file", file);
    setBusy(true); setStatus(null);
    try {
      await api.post("/admin/audios", fd);
      setStatus({ ok: true, text: `Áudio “${title.trim()}” cadastrado com sucesso. Já pode ouvir na lista acima.` });
      toast.success("Áudio cadastrado."); setTitle(""); setCats([]); setFile(null); reload();
    } catch (err) {
      const msg = err.response ? errMsg(err) : "Sem conexão com o servidor. Verifique a internet e tente de novo.";
      setStatus({ ok: false, text: `Não foi possível cadastrar: ${msg}` });
    }
    setBusy(false);
  };
  const del = async (id) => {
    try { await api.delete(`/admin/audios/${id}`); reload(); toast.success("Áudio excluído."); } catch (err) { toast.error(errMsg(err)); }
  };
  return (
    <section className="space-y-3">
      <p className="text-sm text-muted-foreground">Somente gravações reais enviadas pela igreja. O sistema não cria nem imita vozes.</p>
      {audios.length === 0 ? <Empty testId="audios-empty">Nenhum áudio oficial cadastrado.</Empty> :
        <ul className="divide-y divide-zap-line rounded-lg border border-zap-line bg-white">{audios.map((a) => <AudioRow key={a.id} a={a} onDelete={del} />)}</ul>}
      <form onSubmit={upload} className="space-y-2 rounded-lg border border-zap-line bg-white p-4">
        <p className="font-semibold">Cadastrar áudio gravado</p>
        <Input data-testid="audio-title-input" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Título (ex.: Bênção Deus Proverá)" maxLength={80} />
        <div className="flex flex-wrap gap-x-4 gap-y-1">
          {LIB_CATS.map(([k, l]) => (
            <label key={k} className="flex items-center gap-1.5 text-sm"><input type="checkbox" data-testid={`audio-cat-${k}`} checked={cats.includes(k)} onChange={(e) => setCats(e.target.checked ? [...cats, k] : cats.filter((c) => c !== k))} /> {l}</label>
          ))}
        </div>
        <input ref={ref} type="file" accept={AUDIO_ACCEPT} className="hidden" data-testid="audio-file-input" onChange={pick} />
        <div className="flex flex-wrap items-center gap-2">
          <Button type="button" variant="outline" data-testid="audio-choose" onClick={() => ref.current.click()}><Upload className="mr-2 h-4 w-4" /> Escolher arquivo</Button>
          {file ? (
            <span data-testid="audio-file-name" className="flex min-w-0 items-center gap-1.5 rounded-md bg-zap-mist px-2 py-1 text-sm font-medium">
              <FileAudio className="h-4 w-4 shrink-0 text-zap-deep" /><span className="truncate">{file.name}</span>
              <span className="shrink-0 text-xs text-muted-foreground">({fmtSize(file.size)})</span>
            </span>
          ) : <span className="text-sm text-muted-foreground">Nenhum arquivo. Aceita OGG, OPUS, MPEG (WhatsApp), MP3, WAV, M4A e outros, até 16 MB.</span>}
        </div>
        {status && (
          <p data-testid={status.ok ? "audio-upload-success" : "audio-upload-error"} role="status"
            className={`flex items-start gap-1.5 rounded-md px-2 py-1.5 text-sm ${status.ok ? "bg-green-50 text-green-800" : "bg-red-50 text-red-800"}`}>
            {status.ok ? <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0" /> : <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />}{status.text}
          </p>
        )}
        <Button data-testid="audio-save" disabled={busy}>{busy ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Enviando…</> : "Cadastrar áudio"}</Button>
      </form>
    </section>
  );
};

export default function Library() {
  const { api } = useOutletContext();
  const [tab, setTab] = useState("respostas");
  const [audios, setAudios] = useState([]);
  const reload = useCallback(() => api.get("/admin/audios").then((r) => setAudios(r.data)).catch(() => {}), [api]);
  useEffect(() => { reload(); }, [reload]);
  const t = (k, l) => <button data-testid={`library-tab-${k}`} onClick={() => setTab(k)} className={`rounded-md px-3 py-1.5 text-sm font-medium ${tab === k ? "bg-zap-deep text-white" : "bg-zap-mist hover:bg-zap-line"}`}>{l}</button>;
  return (
    <div className="flex h-full flex-col">
      <PageHeader title="Biblioteca de respostas" description="Textos, áudios, vídeos e links oficiais da igreja.">{t("respostas", "Respostas")}{t("audios", "Áudios oficiais")}{t("videos", "Vídeos")}{t("links", "Links prontos")}</PageHeader>
      <div className="scroll-thin min-h-0 flex-1 overflow-y-auto"><div className="max-w-4xl px-5 py-6 md:px-8">{tab === "respostas" ? <Replies audios={audios} /> : tab === "videos" ? <VideoLibrary /> : tab === "links" ? <SavedLinks /> : <Audios audios={audios} reload={reload} />}</div></div>
    </div>
  );
}
